import classNames from 'classnames';
import type Quill from 'quill/core';
import React, { useEffect, useState } from 'react';
import type { ReferenceChildrenProps } from 'react-popper';
import PopOver from '../../components/deprecated/PopOver';
import type { DropdownMenuSection } from '../../components/DropdownMenu';
import DropdownMenu, { getItemByIndex } from '../../components/DropdownMenu';
import type {
  DropdownMenuItemType,
  RenderItemFn,
} from '../../components/DropdownMenuItem';
import addTestId from '../../utils/addTestId';
import isSize from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import useEventListener from '../../utils/useEventListener';
import styles from './PrefixTriggeredMenu.module.scss';

const MENU_HEIGHT = isSize(Sizes.SM) ? 200 : 300;
const OVERRIDE_SHORTCUTS = [
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
  'Enter',
  'Escape',
];

interface PrefixTriggeredMenuProps<T> {
  quill: Quill;
  onSelect: (item: DropdownMenuItemType<T>) => void;
  sections: DropdownMenuSection<T>[];
  query: string;
  onClose: () => void;
  bounds: {
    left: number;
    height: number;
    top: number;
  };
  renderItem: RenderItemFn<T>;
  containerSize?: 'small' | 'regular';
  testId?: string;
}

const PrefixTriggeredMenu = <T,>({
  quill,
  onSelect,
  sections,
  query,
  bounds,
  onClose,
  renderItem,
  containerSize = 'regular',
  testId,
}: PrefixTriggeredMenuProps<T>) => {
  const isEmpty = !sections.some(
    (section) => section.items && section.items.length > 0,
  );
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [query]);

  useEffect(() => {
    if (/^\s/.test(query)) {
      onClose();
    }
  }, [query, onClose]);

  const handleSelect = (item: DropdownMenuItemType<T>) => {
    onSelect(item);
    onClose();
  };

  useEffect(() => {
    if (isEmpty) return undefined;

    const binding = { key: OVERRIDE_SHORTCUTS, handler: () => false };
    // @ts-expect-error
    quill.keyboard.prependBinding(binding);
    return () =>
      // @ts-expect-error
      quill.keyboard.removeBinding(binding);
  }, [quill, isEmpty]);

  useEffect(() => {
    if (isEmpty && query.includes(' ')) {
      onClose();
    }
  }, [isEmpty, query, onClose]);

  useEventListener(
    quill.root,
    'keydown',
    (event) => {
      if (event.key === 'ArrowRight' || event.key === 'Tab') {
        const item = getItemByIndex(sections, index);
        if (item) {
          handleSelect(item);
        }
      }
    },
    { skip: isEmpty },
  );

  return isEmpty ? null : (
    <PopOver
      placement="bottom-start"
      popOverPortal={document.body}
      popOver={
        <div
          data-no-print
          className={classNames(styles.container, {
            [styles.small]: containerSize === 'small',
          })}
          {...addTestId(testId)}
        >
          <DropdownMenu
            sections={sections}
            index={index}
            onIndexChange={setIndex}
            onSelect={handleSelect}
            onClose={onClose}
            inputContainer={quill.root}
            maxHeight={MENU_HEIGHT}
            renderItem={renderItem}
          />
        </div>
      }
    >
      {({ ref }: ReferenceChildrenProps) => (
        <div
          ref={ref}
          className={styles.reference}
          style={{
            left: bounds.left,
            top: bounds.top,
            height: bounds.height,
          }}
        />
      )}
    </PopOver>
  );
};

export default PrefixTriggeredMenu;
